import { useState, useEffect } from 'react';
import UserStore from 'base/user/user-store';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCES_QUERY, CADENCE_LIST_QUERY } from '../utils/cadence-queries';

/** Show only items the user owns or the backend explicitly granted canView. */
const isVisible = (n, currentUserId) => {
  if (!n?.id) return false;
  return n.canView === true || (currentUserId && String(n.ownerUserId) === currentUserId);
};

/** Normalize cadenceType to lowercase — backend returns uppercase (e.g. "CADENCE"). */
const normalizeCadenceType = (c) => ({
  ...c,
  cadenceType: c.cadenceType?.toLowerCase() || c.cadenceType,
});

/**
 * Fetch cadences using the legacy CADENCES_QUERY (returns all fields, no pagination).
 * Used when no pagination parameter is provided.
 *
 * @deprecated Pass a `pagination` option to useCadences to use the paginated path instead.
 *   New callers should not use this path — it loads all cadences in one request and will
 *   degrade as the dataset grows.
 */
const fetchLegacy = async () => {
  const currentUserId = String(UserStore.getUser()?.id ?? '');
  const data = await cadenceGqlCall(CADENCES_QUERY);
  return {
    cadences: (data.cadences || []).filter((n) => isVisible(n, currentUserId)).map(normalizeCadenceType),
    totalCount: null,
    pageInfo: null,
  };
};

/**
 * Fetch cadences using the CADENCE_LIST_QUERY (Connection spec, cursor-based pagination).
 * The query requests full CADENCE_FIELDS + events/edges/metrics on each node,
 * so no per-node detail fetches are needed.
 */
const fetchPaginated = async (pagination, filter) => {
  const variables = { pagination };
  if (filter) variables.filter = filter;
  const currentUserId = String(UserStore.getUser()?.id ?? '');
  const data = await cadenceGqlCall(CADENCE_LIST_QUERY, variables);
  const connection = data.cadenceList || {};
  const nodes = (connection.edges || []).map((edge) => edge?.node).filter((n) => isVisible(n, currentUserId));

  return {
    cadences: nodes.map(normalizeCadenceType),
    totalCount: connection.totalCount ?? null,
    pageInfo: connection.pageInfo ?? null,
  };
};

/**
 * Fetch cadences the current user can access.
 *
 * @param {Object} [options]
 * @param {Object} [options.pagination] - Connection pagination input ({ first, after, last, before }).
 *   When omitted, uses the legacy CADENCES_QUERY that returns all cadences with full fields.
 *   When provided, uses the new CADENCE_LIST_QUERY with cursor-based pagination.
 * @param {boolean} [options.enabled=true] - When false, skips the fetch entirely.
 * @returns {{ cadences, loading, error, refetch, totalCount, pageInfo }}
 */
const useCadences = ({ pagination, filter, enabled = true } = {}) => {
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  // Serialize pagination/filter to detect changes
  const paginationKey = pagination ? JSON.stringify(pagination) : '';
  const filterKey = filter ? JSON.stringify(filter) : '';

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setTotalCount(null);
    setPageInfo(null);
    setCadences([]);

    const doFetch = pagination ? () => fetchPaginated(pagination, filter) : fetchLegacy;

    doFetch()
      .then((result) => {
        if (cancelled) return;
        setCadences(result.cadences);
        setTotalCount(result.totalCount);
        setPageInfo(result.pageInfo);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to fetch cadences');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, paginationKey, filterKey, enabled]);

  const refetch = () => setFetchKey((k) => k + 1);

  return { cadences, loading, error, refetch, totalCount, pageInfo };
};

export default useCadences;
